import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { formatWithdrawWindow, useAppToast } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import { IPickupEmailsStats } from '../../../services/Contests/interfaces'

interface IContestsSendPickupEmailsModalProps {
  isOpen: boolean
  onClose: () => void
  contestId: number
  contestName: string
  onSent?: () => void
}

export default function ContestsSendPickupEmailsModal({
  isOpen,
  onClose,
  contestId,
  contestName,
  onSent
}: IContestsSendPickupEmailsModalProps) {
  const toast = useAppToast()
  const contestService = useMemo(() => new ContestsService(), [])
  const [stats, setStats] = useState<IPickupEmailsStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [force, setForce] = useState(false)
  const [error, setError] = useState(false)
  const confirmDialog = useDisclosure()

  // Reloads the preview; a cancellation flag drops a stale response when `force`
  // is toggled twice quickly, so the last click always wins.
  const loadPreview = useCallback(() => {
    if (!contestId) return undefined

    let active = true
    setError(false)
    setLoading(true)
    contestService
      .getPickupEmailsPreview(contestId, force)
      .then((data) => {
        if (active) setStats(data)
      })
      .catch(() => {
        if (!active) return
        setError(true)
        setStats(null)
        toast.error('Erro ao carregar os destinatários!')
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [contestId, force, contestService, toast])

  useEffect(() => {
    if (!isOpen) return
    return loadPreview()
  }, [isOpen, loadPreview])

  const handleClose = () => {
    setForce(false)
    setStats(null)
    setError(false)
    onClose()
  }

  const handleSend = async () => {
    setSending(true)

    try {
      const result = await contestService.sendPickupEmails(contestId, force)

      toast.success(
        'Envio de e-mails de retirada agendado!',
        `${result.recipients} destinatário(s) para ${result.artworks_to_notify} obra(s). A coluna "Aviso de Retirada" é atualizada quando o envio for processado.`
      )

      confirmDialog.onClose()
      onSent?.()
      handleClose()
    } catch {
      toast.error('Erro ao enviar os e-mails de retirada!')
    } finally {
      setSending(false)
    }
  }

  // The API sends plain "YYYY-MM-DD" values; the helper strips an ISO time like the
  // rest of the panel does. Both ends are required — the same rule the API enforces.
  const withdrawWindow = formatWithdrawWindow(stats?.withdraw_start_date, stats?.withdraw_end_date)
  const hasWithdrawWindow = !!stats?.withdraw_start_date && !!stats?.withdraw_end_date
  const canSend = !!stats && hasWithdrawWindow && stats.recipients > 0 && !loading && !sending

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Enviar e-mails de retirada"
        footer={
          <>
            <Checkbox isChecked={force} onChange={(e) => setForce(e.target.checked)} isDisabled={sending}>
              <Text fontSize="sm">Reenviar para quem já recebeu</Text>
            </Checkbox>

            <Button
              variant="magenta"
              type="button"
              onClick={confirmDialog.onOpen}
              isLoading={sending}
              loadingText="Enviando..."
              isDisabled={!canSend}
            >
              Enviar e-mails
            </Button>
          </>
        }
      >
        <Text variant="caption" mb={4}>
          Avisa os compradores das obras arrematadas e os artistas das demais obras que ainda estão na ABRA de que elas
          já podem ser retiradas — <strong>{contestName}</strong>.
        </Text>

        {loading && <LoadingBlock />}

        {!loading && error && (
          <Alert status="error" borderRadius="md" alignItems="center">
            <AlertIcon />
            <Text fontSize="sm">Não foi possível carregar os destinatários.</Text>
            <Button size="sm" ml="auto" onClick={loadPreview}>
              Tentar novamente
            </Button>
          </Alert>
        )}

        {!loading && stats && (
          <>
            {!hasWithdrawWindow ? (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                Este concurso não tem o período de retirada das obras definido. Preencha as datas inicial e final no
                cadastro do concurso antes de enviar.
              </Alert>
            ) : (
              <Alert status="info" borderRadius="md" mb={4}>
                <AlertIcon />
                Período de retirada: <strong>&nbsp;{withdrawWindow}</strong>
              </Alert>
            )}

            <Flex gap={4} wrap="wrap" mt={2}>
              <Stat minW="120px">
                <StatLabel fontSize="xs">Compradores</StatLabel>
                <StatNumber fontSize="2xl">{stats.buyers}</StatNumber>
              </Stat>
              <Stat minW="120px">
                <StatLabel fontSize="xs">Artistas</StatLabel>
                <StatNumber fontSize="2xl">{stats.applicants}</StatNumber>
              </Stat>
              <Stat minW="120px">
                <StatLabel fontSize="xs">Obras a avisar</StatLabel>
                <StatNumber fontSize="2xl">{stats.artworks_to_notify}</StatNumber>
              </Stat>
            </Flex>

            <Divider my={4} />

            <Box fontSize="sm" color="gray.600">
              <Text>
                Obras ainda na ABRA (recebidas e não devolvidas): <strong>{stats.artworks_in_stock}</strong>
              </Text>
              <Text>
                Já notificadas anteriormente: <strong>{stats.already_sent}</strong>
              </Text>
            </Box>

            {stats.missing_email > 0 && (
              <Alert status="warning" borderRadius="md" mt={4} fontSize="sm">
                <AlertIcon />
                {stats.missing_email} obra(s) sem e-mail do destinatário. Elas não serão notificadas — preencha os dados
                do comprador ou do artista e envie novamente.
              </Alert>
            )}

            {stats.recipients === 0 && hasWithdrawWindow && (
              <Alert status="info" borderRadius="md" mt={4} fontSize="sm">
                <AlertIcon />
                Nenhum destinatário pendente.
                {stats.already_sent > 0 && ' Marque "reenviar" abaixo para avisar quem já foi notificado.'}
              </Alert>
            )}
          </>
        )}
      </FormModal>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        onConfirm={handleSend}
        action="send"
        title="Confirmar envio de e-mails"
        description={`Deseja enviar ${stats?.recipients ?? 0} e-mail(s) de retirada${
          force ? ', INCLUINDO quem já foi notificado' : ''
        }? Esta ação não pode ser desfeita.`}
        confirmLabel="Enviar"
        isLoading={sending}
      />
    </>
  )
}
